<template>
  <ViewContainer :title="$t('recognitions.recognition_other')">
    <v-row class="ma-0 pa-0 mb-4">
      <v-col cols="12" md="12" class="px-0">
        <RecognitionsFilters
          data-testid="recognitionsFilters-text-recognitionsTitle"
          @filter="onFilterChange"
        />
      </v-col>
    </v-row>
    <v-row class="ma-0 pa-0">
      <v-col cols="12" md="8" class="pl-0">
        <v-card class="py-6">
          <v-card-title class="ma-0 pa-0 px-6 mb-3">
            <p
              class="headline font-weight-700 blue-grey--text text--darken-3 mb-0"
              data-testid="recognitions-text-recognizeSomeoneTitle"
            >
              {{ $t('recognitions.recognize_someone') }}
            </p>
          </v-card-title>
          <v-card-subtitle
            class="ma-0 pa-0 px-6 mb-4"
            data-testid="recognitions-text-recognizeSomeoneSubtitle"
          >
            <p class="body-1 blue-grey--text text--darken-2">
              {{ $t('recognitions.recognize_subtitle') }}
            </p>
          </v-card-subtitle>
          <div class="px-6">
            <RecognitionComposer
              outlined
              rows="3"
              data-testid="recognitions-component-recognitionComposer"
              :submitButtonText="$t('recognitions.recognize')"
              @submit="createRecognition"
            />
          </div>
          <v-divider class="my-6" />
          <RecognitionsList
            data-testid="recognitions-component-recognitionsList"
            :isLoading="loading"
            :recognitions="recognitions"
            :totalPages="totalPages"
            :currentPage="currentPage"
            :hasActiveFilters="hasActiveFilters"
            section="recognitions-landing-page"
            @pageChange="handlePageChange"
            @recognitionEdited="fetchRecognitions"
            @recognitionDeleted="updateRecognitions"
          />
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="4"
        :class="{ 'mt-6': $vuetify.breakpoint.smAndDown }"
        class="px-0"
      >
        <AccountSummary
          data-testid="recognitions-component-accountSummary"
          ref="accountSummary"
          :account="account"
          :dateRange="filters.createdAt"
        />
        <MostRecognized
          data-testid="recognitions-component-mostRecognized"
          ref="mostRecognized"
          class="mt-4"
          :dateRange="filters.createdAt"
        />
        <TopRecognizers
          data-testid="recognitions-component-topRecognizers"
          ref="topRecognizers"
          class="mt-4"
          :dateRange="filters.createdAt"
        />
      </v-col>
    </v-row>
  </ViewContainer>
</template>
<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import RecognitionsFilters from './RecognitionsFilters.vue'
import RecognitionComposer from '@/components/RecognitionComposer.vue'
import AccountSummary from './AccountSummary.vue'
import RecognitionsList from './RecognitionsList.vue'
import MostRecognized from './MostRecognized.vue'
import TopRecognizers from './TopRecognizers.vue'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import api from '@/api'
const ITEMS_PER_PAGE = 10

export default {
  name: 'RecognitionsView',

  mixins: [mixin, trackerMixin],

  components: {
    RecognitionComposer,
    ViewContainer,
    RecognitionsFilters,
    AccountSummary,
    RecognitionsList,
    MostRecognized,
    TopRecognizers,
  },

  data() {
    return {
      loading: false,
      filters: {},
      recognitions: [],
      totalPages: 0,
      currentPage: 1,
      ITEMS_PER_PAGE,
    }
  },

  watch: {
    filters: {
      handler() {
        this.fetchRecognitions()
      },
      deep: true,
      immediate: true,
    },
  },

  computed: {
    ...mapGetters(['organizationId', 'account']),
    hasActiveFilters() {
      return Boolean(
        this.filters.account ||
          this.filters.type ||
          this.allRecognitionsOf ||
          this.filters.createdAt?.from ||
          this.filters.createdAt?.to
      )
    },
  },

  methods: {
    async handlePageChange(page) {
      this.currentPage = page
      await this.fetchRecognitions()
    },

    async fetchRecognitions() {
      try {
        this.loading = true
        const response = await api.recognition.getAll(this.organizationId, {
          skip: (this.currentPage - 1) * ITEMS_PER_PAGE,
          limit: ITEMS_PER_PAGE,
          filters: this.filters,
          allRecognitionsOf: this.allRecognitionsOf,
        })
        this.recognitions = response?.data?.results

        this.totalPages =
          Math.ceil(response?.data?.totalRecordsInData / ITEMS_PER_PAGE) || 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async createRecognition({ message, accountIds }) {
      if (!accountIds.length)
        return this.showError(this.$t('recognitions.select_someone'))
      try {
        this.loading = true
        await api.recognition.create(this.organizationId, {
          content: message,
          accounts: accountIds,
        })
        this.showSuccess(
          this.$t('recognitions.recognition_send_successWithCount', {
            count: accountIds.length,
          })
        )
        this.currentPage = 1
        this.updateRecognitions()
        this.$track('recognitions.Create', this.$store, { status: 'success' })
      } catch (err) {
        this.$track('recognitions.Create', this.$store, { status: 'fail' })
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    onFilterChange({ filters, allRecognitionsOf = null }) {
      if (_.isEmpty(filters) && !allRecognitionsOf) {
        this.trackRecognitionsNavigate({
          filter: {
            filters: Object.keys(filters),
            allRecognitionsOf: Boolean(allRecognitionsOf),
          },
        })
      }

      this.filters = filters
      this.allRecognitionsOf = allRecognitionsOf
    },

    updateRecognitions() {
      this.fetchRecognitions()
      this.$refs.accountSummary.fetchAccountSummary()
      this.$refs.mostRecognized.getMostRecognizedAccounts()
      this.$refs.topRecognizers.getTopRecognizersAccounts()
    },
  },
}
</script>
